import type { Command } from "../document/commands.ts";
import { packedToTileRef, tileRefToPacked } from "../document/packed-tile.ts";
import { COLLISION_CELLS_PER_TILE, type MapDocument, type TileRef, type TilesetRef } from "../document/types.ts";
import { floodRegion } from "./flood-fill.ts";
import { requireLayer } from "./panel-commands.ts";
import { zoneTypeAt } from "../document/zones.ts";

/**
 * The fill tool's plans (P1.6-T40): where a fill starts in the active layer's own grid, and the one command
 * that fills the region `floodRegion` finds there. A terrain layer fills its corner vertices, so the tiles
 * resolve from them as they do after a stroke; a tile layer fills cells; a collision layer fills collision
 * cells, and a zone layer the same cells with a type or none. Prop and entity layers take no fill.
 */

/**
 * The largest fill, in tiles, that runs without asking first: a 64 by 64 tile area, four chunks of 32. A
 * region is counted in tiles whatever its layer's grid: on terrain every tile with a corner in the region,
 * a tile cell as one, and `COLLISION_CELLS_PER_TILE` squared collision cells as one, rounded up.
 */
export const FILL_ASK_TILES = 4096;

/**
 * What a fill writes: a corner colour (terrain) or a collision byte, 0 clearing either; a tile, or null to
 * empty the cells; a zone type id, or null to erase zone cells.
 */
export type FillValue = number | TileRef | string | null;

/**
 * A fill ready to run: the command, how many cells of the layer's grid it changes (corners on a terrain
 * layer), the tiles it can change (on terrain every tile with a corner in the region), and whether that is
 * over `FILL_ASK_TILES`.
 */
export interface FillPlan {
  readonly layerId: string;
  readonly command: Command;
  readonly cells: number;
  readonly tiles: number;
  readonly asks: boolean;
}

/**
 * What a fill at a point did: filled the region (its cells in the layer's grid), holds it as the pending
 * fill because it is over the limit (its size in tiles), found the region already holding the value,
 * refused a locked or hidden layer, met a layer that takes no fill, had nothing in hand to fill with (no
 * tile, no collision brush), or lay off the map.
 */
export type FillOutcome =
  | { readonly kind: "filled"; readonly cells: number }
  | { readonly kind: "asks"; readonly tiles: number }
  | { readonly kind: "unchanged" }
  | { readonly kind: "refused"; readonly why: "locked" | "hidden" }
  | { readonly kind: "unsupported" }
  | { readonly kind: "empty hand" }
  | { readonly kind: "off map" };

const MAX_BYTE = 255;

/**
 * The start of a fill under a point: the nearest vertex on a terrain layer (the vertex a stroke paints),
 * the cell under the point on a tile layer and the collision cell under it on a collision layer.
 *
 * @param map The map.
 * @param layerId The layer filled.
 * @param x The point's x, in tiles.
 * @param y The point's y, in tiles.
 * @returns The start's index in the layer's grid, or null off the map or on a layer the fill does not take.
 */
export function fillStart(map: MapDocument, layerId: string, x: number, y: number): number | null {
  const layer = map.layers.find((l) => l.id === layerId);
  if (layer?.kind === "terrain") {
    const vx = Math.round(x);
    const vy = Math.round(y);
    return vx < 0 || vy < 0 || vx > map.width || vy > map.height ? null : vy * (map.width + 1) + vx;
  }
  const scale = layer?.kind === "collision" || layer?.kind === "zone" ? COLLISION_CELLS_PER_TILE : layer?.kind === "tile" ? 1 : 0;
  const cx = Math.floor(x * scale);
  const cy = Math.floor(y * scale);
  const width = map.width * scale;
  return scale === 0 || x < 0 || y < 0 || cx >= width || cy >= map.height * scale ? null : cy * width + cx;
}

/**
 * Plans a fill of the region at a start with a value, as one command that inverts exactly.
 *
 * @param map The map.
 * @param layerId The layer filled: terrain, tile or collision.
 * @param start The start's index in the layer's grid (`fillStart`).
 * @param value The value to write: a byte for terrain and collision, a tile or null for a tile layer.
 * @param tilesets The world's tilesets, which pack tile references.
 * @returns The plan, or null when the region already holds the value.
 * @throws Error when the layer is missing or takes no fill, or the value is not of its kind; RangeError when
 *   the start is outside the layer's grid.
 */
export function planFill(map: MapDocument, layerId: string, start: number, value: FillValue, tilesets: readonly TilesetRef[]): FillPlan | null {
  const layer = requireLayer(map, layerId);
  if (layer.kind === "terrain" || layer.kind === "collision") {
    if (typeof value !== "number" || !Number.isInteger(value) || value < 0 || value > MAX_BYTE) {
      throw new Error(`a ${layer.kind} fill writes a byte, not ${JSON.stringify(value)}`);
    }
    const terrain = layer.kind === "terrain";
    const grid = terrain ? layer.corners : layer.cells;
    const width = terrain ? map.width + 1 : map.width * COLLISION_CELLS_PER_TILE;
    const height = terrain ? map.height + 1 : map.height * COLLISION_CELLS_PER_TILE;
    if (grid[start] === value) {
      return null;
    }
    const region = floodRegion(grid, width, height, start);
    const command: Command = { kind: "fillCells", mapId: map.id, layerId, indices: region, before: grid[start] as number, after: value };
    return planOf(layerId, command, region.length, terrain ? cornerTiles(region, map.width, map.height) : Math.ceil(region.length / (COLLISION_CELLS_PER_TILE * COLLISION_CELLS_PER_TILE)));
  }
  if (layer.kind === "zone") {
    if (value !== null && (typeof value !== "string" || value === "")) {
      throw new Error(`a zone fill writes a type id or none, not ${JSON.stringify(value)}`);
    }
    const held = zoneTypeAt(layer, start);
    if (held === value) {
      return null;
    }
    const region = floodRegion(layer.cells, map.width * COLLISION_CELLS_PER_TILE, map.height * COLLISION_CELLS_PER_TILE, start);
    return planOf(layerId, { kind: "fillCells", mapId: map.id, layerId, indices: region, before: held, after: value }, region.length, Math.ceil(region.length / (COLLISION_CELLS_PER_TILE * COLLISION_CELLS_PER_TILE)));
  }
  if (layer.kind === "tile") {
    if (typeof value === "number" || typeof value === "string") {
      throw new Error(`a tile fill writes a tile or none, not ${JSON.stringify(value)}`);
    }
    const packed = tileRefToPacked(value, tilesets);
    if (layer.tiles[start] === packed) {
      return null;
    }
    const region = floodRegion(layer.tiles, map.width, map.height, start);
    const before = packedToTileRef(layer.tiles[start] as number, tilesets);
    return planOf(layerId, { kind: "fillCells", mapId: map.id, layerId, indices: region, before, after: value }, region.length, region.length);
  }
  throw new Error(`layer ${layerId} is a ${layer.kind} layer, which takes no fill`);
}

/**
 * How many tiles a region of terrain corners touches: every tile with one of its four corners in the region,
 * the tiles whose art the fill can change.
 *
 * @param region Corner indices in a `(width + 1)` by `(height + 1)` grid.
 * @param width Map width in tiles.
 * @param height Map height in tiles.
 * @returns The count.
 */
function cornerTiles(region: Readonly<Uint32Array>, width: number, height: number): number {
  const stride = width + 1;
  const touched = new Uint8Array(width * height);
  let count = 0;
  for (let i = 0; i < region.length; i += 1) {
    const index = region[i] as number;
    const vx = index % stride;
    const vy = (index - vx) / stride;
    for (let ty = Math.max(0, vy - 1); ty <= Math.min(height - 1, vy); ty += 1) {
      for (let tx = Math.max(0, vx - 1); tx <= Math.min(width - 1, vx); tx += 1) {
        const tile = ty * width + tx;
        if (touched[tile] === 0) {
          touched[tile] = 1;
          count += 1;
        }
      }
    }
  }
  return count;
}

/**
 * A plan from its command and counts.
 *
 * @param layerId The layer filled.
 * @param command The command.
 * @param cells Cells of the layer's grid it changes.
 * @param tiles The same in tiles.
 * @returns The plan.
 */
function planOf(layerId: string, command: Command, cells: number, tiles: number): FillPlan {
  return { layerId, command, cells, tiles, asks: tiles > FILL_ASK_TILES };
}
